import {
    disposeBatch,
    markBatchQc,
    type BatchDisposition,
    type BucketedBatchLine,
} from '@api/vendor-portal';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    Group,
    Modal,
    NumberInput,
    Radio,
    Stack,
    Text,
    Textarea,
} from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useEffect, useState, type FC } from 'react';

/**
 * `RELEASE` puts a failed batch back into use, `REMOVE` records it physically leaving, `FAIL`
 * withdraws a batch that was already released.
 *
 * All three are one modal because all three are "explain what just happened to this batch".
 * The ordinary verdict on a batch still IN QC needs no explanation and fires straight from the
 * row — that is a checker doing their job. Failing something already released is a different
 * act: the stock is in use, blocking it is immediate, and the reason is the only thing that
 * will explain the gap to anyone reading later.
 */
export type BatchAction = 'RELEASE' | 'REMOVE' | 'FAIL';

interface BatchActionModalProps {
    action: BatchAction | null;
    batch: BucketedBatchLine | null;
    /** Finished goods have no supplier to go back to, so the choice collapses to one. */
    isMaterial: boolean;
    onClose: () => void;
    onDone: () => void;
}

/**
 * The two things that need saying out loud.
 *
 * A reason is demanded here rather than merely invited. Both of these actions destroy the
 * ordinary reading of a batch's history — one puts rejected material back into product, the
 * other writes stock off with no document behind it — and six months later the only thing
 * standing between the record and a shrug is what somebody typed in this box.
 *
 * The backend refuses both without a reason regardless. Asking here just means the person finds
 * out while they still remember why.
 */
const BatchActionModal: FC<BatchActionModalProps> = ({
    action,
    batch,
    isMaterial,
    onClose,
    onDone,
}) => {
    const { showToastSuccess, showToastError } = useToaster();

    const [reason, setReason] = useState('');
    const [quantity, setQuantity] = useState<number | string>('');
    const [disposition, setDisposition] = useState<BatchDisposition>('DISPOSE');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Default the quantity to the whole batch: a failed lot usually leaves in one piece, and a
    // part-disposal is the exception worth typing.
    useEffect(() => {
        if (!batch) return;
        setReason('');
        setQuantity(batch.quantity);
        setDisposition(isMaterial ? 'RETURN_TO_SUPPLIER' : 'DISPOSE');
    }, [batch, isMaterial]);

    if (!action || !batch) return null;

    const isRemove = action === 'REMOVE';
    const isFail = action === 'FAIL';
    const quantityNumber = typeof quantity === 'number' ? quantity : Number(quantity);
    const canSubmit =
        reason.trim().length > 0 &&
        (!isRemove || (quantityNumber > 0 && quantityNumber <= batch.quantity));

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (isRemove) {
                await disposeBatch(batch.batchId, {
                    disposition,
                    quantity: quantityNumber,
                    reasonCode: reason.trim(),
                });
                showToastSuccess({
                    title:
                        disposition === 'RETURN_TO_SUPPLIER'
                            ? `${quantityNumber} returned to supplier`
                            : `${quantityNumber} written off`,
                });
            } else if (isFail) {
                const { notable } = await markBatchQc(batch.batchId, {
                    qcStatus: 'FAILED',
                    notes: reason.trim(),
                });
                showToastSuccess({
                    title: `${batch.batchCode ?? 'Batch'} withdrawn`,
                    subtitle: notable
                        ? 'The brand is shown this and the reason for it. Nothing can be run from it now.'
                        : 'Nothing can be run from it now.',
                });
            } else {
                const { notable } = await markBatchQc(batch.batchId, {
                    qcStatus: 'PASSED',
                    notes: reason.trim(),
                });
                showToastSuccess({
                    title: `${batch.batchCode ?? 'Batch'} released`,
                    subtitle: notable
                        ? 'The brand is shown this release and the reason for it.'
                        : 'It can now be used in production.',
                });
            }
            onDone();
            onClose();
        } catch (err: any) {
            showToastError({
                title: isRemove
                    ? 'Could not record it'
                    : isFail
                      ? 'Could not withdraw the batch'
                      : 'Could not release the batch',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            opened
            onClose={onClose}
            centered
            size="md"
            title={
                <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                    {isRemove
                        ? 'Remove blocked stock'
                        : isFail
                          ? 'Withdraw this batch from use'
                          : 'Release a failed batch'}
                </Text>
            }
        >
            <Stack gap={16}>
                <Text fz={13} c={neutralColors.textMuted}>
                    {batch.batchCode ?? 'No batch code'} · {batch.quantity} on site
                </Text>

                {isFail && (
                    <Text fz={13} c={semanticColors.warning.dark}>
                        This batch has already passed QC and is in use. Withdrawing it blocks the
                        whole {batch.quantity} immediately — nothing can be run from it until it
                        is released again.
                    </Text>
                )}

                {!isRemove && !isFail && (
                    <Text fz={13} c={semanticColors.warning.dark}>
                        This batch failed QC. Releasing it makes it usable again and puts it back
                        in reach of production.
                    </Text>
                )}

                {isRemove && isMaterial && (
                    <Radio.Group
                        label="What happened to it?"
                        value={disposition}
                        onChange={(value) => setDisposition(value as BatchDisposition)}
                    >
                        <Stack gap={8} mt={8}>
                            <Radio value="RETURN_TO_SUPPLIER" label="Sent back to the supplier" />
                            <Radio value="DISPOSE" label="Destroyed or written off here" />
                        </Stack>
                    </Radio.Group>
                )}

                {isRemove && (
                    <NumberInput
                        label="How much"
                        value={quantity}
                        onChange={setQuantity}
                        min={0}
                        max={batch.quantity}
                        description={`Up to ${batch.quantity}. Part of a batch can go back and the rest be written off.`}
                    />
                )}

                <Textarea
                    label="Why"
                    placeholder={
                        isRemove
                            ? 'Damaged in storage, supplier recall, past expiry…'
                            : isFail
                              ? 'Customer complaint, retest failed, supplier recall notice…'
                              : 'Retested clean, wrong lot was marked, lab report attached…'
                    }
                    value={reason}
                    onChange={(e) => setReason(e.currentTarget.value)}
                    minRows={3}
                    autosize
                />

                <Group justify="flex-end" gap={12}>
                    <Button variant={ButtonVariant.DEFAULT} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant={
                            isRemove || isFail
                                ? ButtonVariant.FILLED_DESTRUCTIVE
                                : ButtonVariant.ACTION
                        }
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        loading={isSubmitting}
                    >
                        {isRemove ? 'Record it' : isFail ? 'Withdraw it' : 'Release the batch'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default BatchActionModal;
